import React, { useState, useMemo } from 'react';
import { Box, Paper, Tab, Tabs, Typography, Divider, Chip } from '@mui/material';

/**
 * Render generated PRA letters as tabs formatted like an email client.
 * Accepts a `request` object with subject/generated_request/split_parts/email_to.
 */
const LettersTabView = ({ request, senderEmail }) => {
  const letters = useMemo(() => {
    const out = [];
    if (request?.is_split_request && Array.isArray(request.split_parts) && request.split_parts.length > 0) {
      request.split_parts.forEach((part, idx) => {
        out.push({
          title: part.title || part.subject || `Part ${idx + 1}`,
          subject: part.subject || request.subject || 'Public Records Request',
          to: part.email_to || request.email_to || '',
          body: part.body || part.content || part.generated_request || '',
          destination_name: part.destination_name || request.destination,
        });
      });
    } else if (request?.generated_request) {
      out.push({
        title: 'Letter',
        subject: request.subject || 'Public Records Request',
        to: request.email_to || '',
        body: request.generated_request,
        destination_name: request.destination,
      });
    }
    return out;
  }, [request]);

  const [tab, setTab] = useState(0);

  if (letters.length === 0) {
    return null;
  }

  const letter = letters[tab];
  const fromAddr = senderEmail || 'not yet sent';
  const sentDate = request?.sent_date ? new Date(request.sent_date).toLocaleString() : 'Draft';

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #e0ddd6',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 3,
      }}
    >
      {letters.length > 1 && (
        <Box sx={{ borderBottom: '1px solid #e0ddd6', backgroundColor: '#fafaf5' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontWeight: 600,
                fontSize: '0.82rem',
                minHeight: 36,
                py: 0.5,
              },
            }}
          >
            {letters.map((l, i) => (
              <Tab key={i} label={l.title} />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Email-client-style header */}
      <Box sx={{ p: 2.25, backgroundColor: '#ffffff' }}>
        <Typography
          sx={{
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            fontSize: '1.05rem',
            color: '#00075e',
            mb: 1.25,
            lineHeight: 1.3,
          }}
        >
          {letter.subject}
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: '70px 1fr', rowGap: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.75rem', color: '#838996', fontWeight: 600 }}>From</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#00075e', fontFamily: '"SF Mono", ui-monospace, monospace' }}>
            {fromAddr}
          </Typography>

          <Typography sx={{ fontSize: '0.75rem', color: '#838996', fontWeight: 600 }}>To</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#00075e', fontFamily: '"SF Mono", ui-monospace, monospace' }}>
            {letter.to || '—'}
            {letter.destination_name && (
              <Chip
                size="small"
                label={letter.destination_name}
                sx={{ ml: 1, height: 18, fontSize: '0.68rem' }}
              />
            )}
          </Typography>

          <Typography sx={{ fontSize: '0.75rem', color: '#838996', fontWeight: 600 }}>Date</Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#00075e' }}>{sentDate}</Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#e0ddd6' }} />

      {/* Body */}
      <Box
        sx={{
          p: 3,
          backgroundColor: '#ffffff',
          whiteSpace: 'pre-wrap',
          fontFamily: 'Georgia, serif',
          fontSize: '0.95rem',
          color: '#1a1a2e',
          lineHeight: 1.7,
        }}
      >
        {letter.body || 'This letter has not been generated yet.'}
      </Box>
    </Paper>
  );
};

export default LettersTabView;
